import type {
  BriefResponse,
  CaseCardResponse,
  PortfolioResponse,
  PropagationRequest,
  FinancialMetric,
  AnomalyItem,
  PipelineMetadata,
} from "../types";

/**
 * Shape returned by GET /api/audit — the full ripple_audit_export.json.
 * We only type the fields the frontend actually uses.
 */
export interface AuditExportResponse {
  metadata: PipelineMetadata;
  companies: Record<string, {
    ticker: string;
    name: string;
    summary: {
      total_effects: number;
      layer_1: number;
      layer_2: number;
      layer_3: number;
      pass_rate_pct: number;
    };
    financial_metrics?: Record<string, FinancialMetric>;
  }>;
  anomalies?: AnomalyItem[];
}

const API_STORAGE_KEY = "portfolio_api_base";

// Hosts (or host patterns) we'll accept as legitimate API bases.
// Anything else is rejected — both at first-read from the URL parameter
// and on replay from localStorage — to prevent an attacker-crafted
// ?apiBase=https://evil.example.com link from permanently redirecting
// every subsequent fetch to a third-party server.
const ALLOWED_API_HOST_PATTERNS: RegExp[] = [
  /^localhost(:\d+)?$/i,
  /^127\.0\.0\.1(:\d+)?$/,
  /^api\.neptech\.us$/i,
  /\.ngrok-free\.app$/i,
  /\.ngrok\.io$/i,
  /\.vercel\.app$/i,
];

function isAllowedHost(host: string): boolean {
  return ALLOWED_API_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

function normalizeApiBase(value?: string | null): string | null {
  if (!value) return null;

  const normalized = value.trim().replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(normalized)) return null;

  try {
    const url = new URL(normalized);
    if (!isAllowedHost(url.host)) {
      if (typeof console !== "undefined") {
        console.warn(`[Ripple] Rejected API base with disallowed host: ${url.host}`);
      }
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}

function resolveRuntimeApiBase(): string | null {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  const queryBase = normalizeApiBase(params.get("apiBase"));
  if (queryBase) {
    window.localStorage.setItem(API_STORAGE_KEY, queryBase);

    params.delete("apiBase");
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${
      nextSearch ? `?${nextSearch}` : ""
    }${window.location.hash}`;
    window.history.replaceState({}, "", nextUrl);

    return queryBase;
  }

  const stored = normalizeApiBase(window.localStorage.getItem(API_STORAGE_KEY));
  if (!stored && window.localStorage.getItem(API_STORAGE_KEY)) {
    // Stored value failed the allow-list — purge so a rejected host doesn't
    // keep triggering the console warning on every page load.
    window.localStorage.removeItem(API_STORAGE_KEY);
  }
  return stored;
}

const API_BASE =
  resolveRuntimeApiBase() ||
  normalizeApiBase(process.env.FINRAG_API_URL) ||
  "http://localhost:5000";

function buildHeaders(extraHeaders?: Record<string, string>): HeadersInit {
  const headers: Record<string, string> = {
    ...(API_BASE.includes("ngrok") ? { "ngrok-skip-browser-warning": "true" } : {}),
    ...(extraHeaders ?? {}),
  };

  return headers;
}

async function parseJson<T>(response: Response, label: string): Promise<T> {
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`${label} failed (${response.status}): ${body || "unknown error"}`);
  }

  return (await response.json()) as T;
}

export function getApiBase(): string {
  return API_BASE;
}

export async function fetchPortfolio(): Promise<PortfolioResponse> {
  const response = await fetch(`${API_BASE}/api/portfolio`, {
    headers: buildHeaders(),
  });
  return parseJson<PortfolioResponse>(response, "Portfolio fetch");
}

export async function runPropagation(
  request: PropagationRequest
): Promise<PortfolioResponse> {
  const response = await fetch(`${API_BASE}/api/propagate`, {
    method: "POST",
    headers: buildHeaders({
      "Content-Type": "application/json",
    }),
    body: JSON.stringify({
      company: request.company,
      fy_current: request.fyCurrent,
      fy_prior: request.fyPrior,
      scenario: request.scenario,
      threshold_pct: request.thresholdPct,
    }),
  });

  return parseJson<PortfolioResponse>(response, "Propagation");
}

export async function fetchBrief(
  company: string,
  fy: string
): Promise<BriefResponse> {
  const params = new URLSearchParams({ company, fy });
  const response = await fetch(`${API_BASE}/api/brief?${params}`, {
    headers: buildHeaders(),
  });
  return parseJson<BriefResponse>(response, "Brief fetch");
}

export async function fetchCaseCards(
  source: string,
  metric: string,
  target: string
): Promise<CaseCardResponse> {
  const params = new URLSearchParams({ source, metric, target });
  const response = await fetch(`${API_BASE}/api/case-cards?${params}`, {
    headers: buildHeaders(),
  });
  return parseJson<CaseCardResponse>(response, "Case card fetch");
}

/**
 * Fetch the full audit export (ripple_audit_export.json) from the backend.
 * Contains financial metrics, anomalies, and per-company summaries.
 * This is the data backbone for the Metrics tab when running live.
 */
export async function fetchAuditExport(): Promise<AuditExportResponse> {
  const response = await fetch(`${API_BASE}/api/audit`, {
    headers: buildHeaders(),
  });
  return parseJson<AuditExportResponse>(response, "Audit export fetch");
}
